import React, {useState} from 'react';
import {Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {getAppConfig, useTrovoConfig} from '../../../utils/config';
import {BeforeYouBegin} from 'src/components/modals/experiments/BeforeYouBegin';
import Popup from 'src/components/popup';
import {getBooleanLocalStorageFlag, LOCAL_STORAGE_FLAGS, setBooleanLocalStorageFlag} from '../../../utils/localStorageFlags';

interface PageState {
    name: {value: string; error: string};
    description: {value: string; error: string};
    organism: {value: string; error: string};
    sequencing_type: {value: string; error: string};
}

const enabledOrganisms: Record<string, string[]> = {
    'RNA Seq': ['Homo sapiens', 'Mus musculus', 'Felis catus', 'Sus scrofa', 'Rattus norvegicus'],
    'ChIP Seq': ['Homo sapiens', 'Mus musculus'],
    'ATAC Seq': ['Homo sapiens', 'Mus musculus']
};

/**
 * Step 1 of the experiment creation process.
 *
 * Makes the user choose a name, description, sequencing type (e.g., RNA Seq),
 * and organism (e.g., Homo sapiens).
 *
 * @param formData used to initialise the fields
 * @param setExperimentDetails will be called when the form is saved and continued
 */
const Step1 = ({
    formData,
    setExperimentDetails
}: {
    formData: {experiment_details: ExperimentDetails};
    setExperimentDetails: (obj: ExperimentDetails) => Promise<{error: string} | void>;
}) => {
    const navigate = useNavigate();
    const [dontShowAgain, setDontShowAgain] = useState(() => getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentBeforeYouBegin));
    const [isBeforeYouBeginOpen, setBeforeYouBeginOpen] = useState(
        () => !getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentBeforeYouBegin)
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {featureFlags} = getAppConfig();
    const {user} = useTrovoConfig();
    const [pageState, setPageState] = useState<PageState>({
        name: {value: formData.experiment_details.name || '', error: ''},
        description: {value: formData.experiment_details.description || '', error: ''},
        organism: {value: formData.experiment_details.organism || '', error: ''},
        sequencing_type: {value: formData.experiment_details.sequencing_type || '', error: ''}
    });
    // If sequencing_type already has a value, it means the user has come back
    // to this step after already making a choice. Do not allow changing that
    // earlier choice. It would lead to confusing data.
    const disableChangingSequenceType = Boolean(formData.experiment_details.sequencing_type);

    const isDemoAccount: boolean = user.cb_item_price_id?.includes('demo');
    const complete = Boolean(pageState.name.value && pageState.organism.value && pageState.sequencing_type.value);
    const anyErrors = Boolean(
        pageState.name.error || pageState.description.error || pageState.organism.error || pageState.sequencing_type.error
    );

    /**
     * Performs validation of fields and updates page state.
     *
     * @param e the event triggering the change
     */
    const handleInputChange = (e: any) => {
        const {name: fieldName, value}: {name: string; value: string} = e.target;

        if (fieldName === 'name') {
            if (value.trim() === '') {
                setPageState({...pageState, name: {value: value, error: 'Name cannot be empty.'}});
            } else {
                setPageState({...pageState, name: {value: value, error: ''}});
            }
        } else if (fieldName === 'sequencing_type') {
            // Clear organism
            setPageState({...pageState, sequencing_type: {value: value, error: ''}, organism: {value: '', error: ''}});
        } else {
            // Uses fieldName variable's content as key prop. Note the `[ ]`
            setPageState({...pageState, [fieldName]: {value: value, error: ''}});
        }
    };

    const handleDontShowAgainChange = (checked: boolean) => {
        setDontShowAgain(checked);
        setBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentBeforeYouBegin, checked);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const res = await setExperimentDetails({
            name: pageState.name.value.trim(),
            description: pageState.description.value.trim(),
            organism: pageState.organism.value,
            sequencing_type: pageState.sequencing_type.value
        });
        // This assumes that we only error on the name field. Currently true,
        // but not ideal. Also if there is just a connection timeout or
        // something, it would also appear at the name field. That's just
        // confusing. Instead use an Error component for the entire form. TODO
        // Pushing that back for now because other changes in branch off of
        // current one rely on this, so keeping it simpler for myself.
        if (res && res.error) {
            setPageState({
                ...pageState,
                name: {value: pageState.name.value.trim(), error: res.error},
                description: {value: pageState.description.value.trim(), error: ''},
                organism: {value: pageState.organism.value, error: ''},
                sequencing_type: {value: pageState.sequencing_type.value, error: ''}
            });
            setIsSubmitting(false);
        }
    };

    return (
        <Box component="form" sx={{'& label': {width: '75ch'}, display: 'flex', flexDirection: 'column', gap: '15px'}}>
            <TextField
                name="name"
                label="Name"
                fullWidth
                inputProps={{maxLength: 128}}
                disabled={isDemoAccount}
                value={pageState.name.value}
                placeholder="Enter a name"
                onChange={handleInputChange}
                error={Boolean(pageState.name.error)}
                helperText={pageState.name.error}
            />

            <TextField
                name="description"
                label="Description (optional)"
                fullWidth
                multiline
                inputProps={{maxLength: 8096}}
                value={pageState.description.value}
                disabled={isDemoAccount}
                placeholder="Enter a description"
                onChange={handleInputChange}
            />
            <FormControl>
                <InputLabel>Sequencing type</InputLabel>
                <Select
                    MenuProps={{
                        PaperProps: {
                            sx: {bgcolor: 'surface-container-low'}
                        }
                    }}
                    name="sequencing_type"
                    label="Sequencing type"
                    disabled={disableChangingSequenceType || isDemoAccount}
                    value={pageState.sequencing_type.value}
                    onChange={handleInputChange}
                    fullWidth
                    required>
                    <MenuItem value="RNA Seq">RNA Seq</MenuItem>
                    {featureFlags.chipSeqEnabled && <MenuItem value="ChIP Seq">ChIP Seq</MenuItem>}
                    {featureFlags.atacSeqEnabled && <MenuItem value="ATAC Seq">ATAC Seq</MenuItem>}
                </Select>
                {disableChangingSequenceType && (
                    <Typography variant="body" size="small" sx={{color: 'on-surface-variant', mb: 2}}>
                        You have selected a sequencing type. This cannot be changed in this experiment.
                    </Typography>
                )}
            </FormControl>
            <FormControl>
                <InputLabel>Select Organism</InputLabel>
                <Select
                    MenuProps={{
                        PaperProps: {
                            sx: {bgcolor: 'surface-container-low'}
                        }
                    }}
                    name="organism"
                    label="Select Organism"
                    value={pageState.organism.value}
                    onChange={handleInputChange}
                    fullWidth
                    disabled={isDemoAccount}
                    required>
                    {enabledOrganisms[pageState.sequencing_type.value]?.map((organism: string) => (
                        <MenuItem key={organism} value={organism}>
                            {organism}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!complete || anyErrors || isDemoAccount || isSubmitting}
                fullWidth>
                Save and Continue
            </Button>
            <Button variant="text" color="primary" onClick={() => navigate(`/`)} fullWidth disabled={isDemoAccount}>
                Save Progress and Exit
            </Button>
            <Popup isOpen={isBeforeYouBeginOpen} onClose={() => setBeforeYouBeginOpen(false)}>
                <BeforeYouBegin
                    onClose={() => setBeforeYouBeginOpen(false)}
                    dontShowAgain={dontShowAgain}
                    onDontShowAgainChange={handleDontShowAgainChange}
                />
            </Popup>
        </Box>
    );
};

export default Step1;
